import { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';

interface SpotlightStep {
  targetSelector: string;
  title: string;
  description: string;
  tooltipPosition: 'bottom' | 'right' | 'left';
}

interface SpotlightOverlayProps {
  steps: SpotlightStep[];
  onComplete: () => void;
  onSkip: () => void;
}

interface Rect {
  left: number;
  top: number;
  width: number;
  height: number;
}

const PADDING = 8;
const TOOLTIP_GAP = 16;
const TOOLTIP_MAX_W = 320;

function clamp(val: number, min: number, max: number) {
  return Math.max(min, Math.min(max, val));
}

function getTooltipStyle(
  rect: Rect | null,
  position: 'bottom' | 'right' | 'left'
): React.CSSProperties {
  const effectiveMaxW =
    typeof window !== 'undefined' ? Math.min(TOOLTIP_MAX_W, window.innerWidth - 16) : TOOLTIP_MAX_W;

  if (!rect) {
    // Fallback: center screen
    return {
      position: 'fixed',
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
      maxWidth: effectiveMaxW,
    };
  }

  const pad = PADDING;
  let top: number;
  let left: number;

  if (position === 'bottom') {
    top = rect.top + rect.height + pad + TOOLTIP_GAP;
    left = rect.left + rect.width / 2 - TOOLTIP_MAX_W / 2;
  } else if (position === 'right') {
    top = rect.top - pad;
    left = rect.left + rect.width + pad + TOOLTIP_GAP;
  } else {
    // left
    top = rect.top - pad;
    left = rect.left - pad - TOOLTIP_GAP - TOOLTIP_MAX_W;
  }

  // Clamp to viewport
  top = clamp(top, 8, window.innerHeight - 260);
  left = clamp(left, 8, window.innerWidth - effectiveMaxW - 8);

  return {
    position: 'fixed',
    top,
    left,
    maxWidth: effectiveMaxW,
  };
}

function getArrowStyle(
  position: 'bottom' | 'right' | 'left'
): React.CSSProperties & { borderColor: string } {
  const base: React.CSSProperties = {
    position: 'absolute',
    width: 0,
    height: 0,
  };

  if (position === 'bottom') {
    return {
      ...base,
      top: -6,
      left: '50%',
      transform: 'translateX(-50%)',
      borderLeft: '6px solid transparent',
      borderRight: '6px solid transparent',
      borderBottom: '6px solid rgb(39 39 42)',
      borderColor: '',
    };
  }
  if (position === 'right') {
    return {
      ...base,
      top: 16,
      left: -6,
      borderTop: '6px solid transparent',
      borderBottom: '6px solid transparent',
      borderRight: '6px solid rgb(39 39 42)',
      borderColor: '',
    };
  }
  // left
  return {
    ...base,
    top: 16,
    right: -6,
    borderTop: '6px solid transparent',
    borderBottom: '6px solid transparent',
    borderLeft: '6px solid rgb(39 39 42)',
    borderColor: '',
  };
}

export function SpotlightOverlay({ steps, onComplete, onSkip }: SpotlightOverlayProps) {
  const { t } = useTranslation();
  const [currentStep, setCurrentStep] = useState(0);
  const [rect, setRect] = useState<Rect | null>(null);
  const [tooltipKey, setTooltipKey] = useState(0);
  const overlayRef = useRef<HTMLDivElement>(null);

  const step = steps[currentStep];

  const measure = useCallback(() => {
    if (!step) return;
    const el = document.querySelector(step.targetSelector);
    if (!el) {
      console.warn('[SpotlightOverlay] Target not found:', step.targetSelector);
      setRect(null);
      return;
    }
    const rawRect = el.getBoundingClientRect();

    // Clamp rect to viewport bounds
    const clampedRect = {
      top: Math.max(0, rawRect.top),
      left: Math.max(0, rawRect.left),
      width: Math.min(rawRect.width, window.innerWidth - Math.max(0, rawRect.left)),
      height: Math.min(
        rawRect.top < 0 ? rawRect.height + rawRect.top : rawRect.height,
        window.innerHeight - Math.max(0, rawRect.top)
      ),
    };
    setRect(clampedRect);
  }, [step]);

  // Measure on step change
  useEffect(() => {
    measure();
    setTooltipKey((k) => k + 1);
  }, [currentStep, measure]);

  // Recalculate on resize / scroll
  useEffect(() => {
    const observer = new ResizeObserver(() => measure());
    observer.observe(document.documentElement);
    window.addEventListener('scroll', measure, true);
    return () => {
      observer.disconnect();
      window.removeEventListener('scroll', measure, true);
    };
  }, [measure]);

  const handleNext = useCallback(() => {
    if (currentStep < steps.length - 1) {
      setCurrentStep((s) => s + 1);
    } else {
      onComplete();
    }
  }, [currentStep, steps.length, onComplete]);

  const handlePrev = useCallback(() => {
    if (currentStep > 0) {
      setCurrentStep((s) => s - 1);
    }
  }, [currentStep]);

  if (!step) return null;

  // Hole coordinates (or off-screen if no target)
  const holeX = rect ? rect.left - PADDING : -9999;
  const holeY = rect ? rect.top - PADDING : -9999;
  const holeW = rect ? rect.width + PADDING * 2 : 0;
  const holeH = rect ? rect.height + PADDING * 2 : 0;

  const tooltipStyle = getTooltipStyle(rect, step.tooltipPosition);
  const arrowStyle = getArrowStyle(step.tooltipPosition);

  return (
    <>
      <style>{`
        @keyframes spotlight-overlay-in {
          from { opacity: 0 }
          to { opacity: 1 }
        }
        @keyframes spotlight-tooltip-in {
          from { opacity: 0 }
          to { opacity: 1 }
        }
      `}</style>
      <div
        ref={overlayRef}
        className="fixed inset-0 z-[70]"
        style={{ animation: 'spotlight-overlay-in 300ms ease-out' }}
      >
        <svg className="absolute inset-0 w-full h-full">
          <defs>
            <mask id="spotlight-mask">
              <rect width="100%" height="100%" fill="white" />
              <rect
                style={{ transition: 'all 300ms ease-in-out' }}
                x={holeX}
                y={holeY}
                width={holeW}
                height={holeH}
                rx={8}
                fill="black"
              />
            </mask>
          </defs>
          <rect width="100%" height="100%" fill="rgba(0,0,0,0.75)" mask="url(#spotlight-mask)" />
        </svg>

        {/* Tooltip */}
        <div
          key={tooltipKey}
          className="bg-zinc-900 border border-zinc-700/50 rounded-xl shadow-2xl shadow-black/60 p-5"
          style={{
            ...tooltipStyle,
            animation: 'spotlight-tooltip-in 200ms ease-out',
            zIndex: 71,
          }}
        >
          {/* Arrow */}
          <div style={arrowStyle} />

          <span className="text-[10px] font-medium text-violet-400 bg-violet-500/10 px-2 py-0.5 rounded">
            {currentStep + 1} / {steps.length}
          </span>
          <h3 className="text-sm font-semibold text-zinc-100 mt-2">{step.title}</h3>
          <p className="text-xs text-zinc-400 mt-1 leading-relaxed">{step.description}</p>

          <div className="flex items-center justify-between mt-4">
            <button
              type="button"
              onClick={onSkip}
              className="text-xs text-zinc-500 hover:text-zinc-300 transition-colors"
            >
              {t('onboarding.spotlight_skip')}
            </button>
            <div className="flex items-center gap-2">
              {currentStep > 0 && (
                <button
                  type="button"
                  onClick={handlePrev}
                  className="text-xs text-zinc-400 hover:text-zinc-200 transition-colors px-2 py-1.5"
                >
                  {t('onboarding.prev_step')}
                </button>
              )}
              <button
                type="button"
                onClick={handleNext}
                className="bg-violet-600 hover:bg-violet-500 text-white text-xs font-medium px-3 py-1.5 rounded-lg transition-colors"
              >
                {currentStep === steps.length - 1
                  ? t('onboarding.lets_go')
                  : t('onboarding.spotlight_next')}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
